// 語音合成工具，用於朗讀文本
// 提供日語假名的朗讀功能，並允許自定義語速和音調
// 核心目標：
// 1. 在 iOS Safari / WKWebView 也能穩定載入 voices
// 2. 確保使用者互動後才播放（符合 Autoplay policy）
// 3. 在 iOS 17+ AudioContext 必須 resume() 才能發聲
// 4. SSR safety：避免在 Next.js 伺端階段觸發 window 取用

/**
 * 朗讀日語假名
 * @param {string} kanaText - 要發音的假名文本
 * @param {Object} options - 可選參數
 * @param {string} options.lang - 語言代碼，默認為日語 (ja-JP)
 * @param {number} options.pitch - 音調，範圍 0-2，默認為 1
 * @param {number} options.rate - 語速，範圍 0.1-10，默認為 1
 * @param {number} options.volume - 音量，範圍 0-1，默認為 1
 * @returns {Promise<SpeechSynthesisUtterance|null>}
 */
export default async function speakKana(kanaText, options = {}) {
  if (!kanaText) return null;

  // 瀏覽器支持檢查
  if (typeof window === "undefined" || !("speechSynthesis" in window)) {
    console.warn("Web Speech API not supported")
    return null;
  }

  const { pitch = 1, rate = 1, volume = 1 } = options;

  // 確保使用者互動
  await waitForUserActivation();

  // 確保 AudioContext 已 resume
  await ensureAudioContext();

  // 確保 voices 可用
  let chosenVoice;
  const voices = await loadVoices("ja-JP");
  if (voices.length) {
    chosenVoice = voices[0];
  }

  const utter = new SpeechSynthesisUtterance(kanaText);
  utter.lang = "ja-JP";
  utter.pitch = Math.min(2, Math.max(0, pitch));
  utter.rate = Math.min(10, Math.max(0.1, rate));
  utter.volume = Math.min(1, Math.max(0, volume));
  if (chosenVoice) {
    utter.voice = chosenVoice;
  }

  window.speechSynthesis.speak(utter);
  return utter;
}

let voicesPromise = null;
let voicesLoaded = false;
let voicesCache = [];

/**
 * 內部：載入語音清單（含 polling 與 voiceschanged 事件），解決 iOS 不會觸發 voiceschanged 的情況。
 */
function loadVoices(langFilter = null, timeout = 3000) {
  if (voicesLoaded) {
    const list = langFilter ? voicesCache.filter(v => v.lang.startsWith(langFilter)) : voicesCache;
    return Promise.resolve(list);
  }

  if (voicesPromise) {
    return voicesPromise;
  }

  voicesPromise = new Promise((resolve) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      resolve([]);
      return;
    }

    const synth = window.speechSynthesis;

    // helper to finish loading
    const finish = () => {
      voicesCache = synth.getVoices();
      if (!voicesCache.length) {
        return; // keep waiting
      }

      voicesLoaded = true;
      clearInterval(pollId);
      synth.removeEventListener("voiceschanged", finish);

      const list = langFilter ? voicesCache.filter(v => v.lang.startsWith(langFilter)) : voicesCache;

      resolve(list);
    };

    // Safari 可能不會觸發 voiceschanged，所以同時用 polling
    synth.addEventListener("voiceschanged", finish);
    const pollId = setInterval(finish, 250);

    // 停止 polling 的保險機制
    setTimeout(() => {
      clearInterval(pollId);
      synth.removeEventListener("voiceschanged", finish);

      const list = synth.getVoices();
      voicesCache = list;
      voicesLoaded = !!list.length;

      const filtered = langFilter ? list.filter(v => v.lang.startsWith(langFilter)) : list;

      resolve(filtered);
    }, timeout);

    // 第一次嘗試
    finish();
  });

  return voicesPromise;
}


let userActivated = false;

/**
 * 內部：等待使用者首次互動，符合 iOS 的「User Activation」要求。
 */
function waitForUserActivation() {
  if (userActivated) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const handler = () => {
      userActivated = true;
      window.removeEventListener("click", handler);
      window.removeEventListener("touchend", handler);
      resolve();
    };

    window.addEventListener("click", handler, { once: true });
    window.addEventListener("touchend", handler, { once: true });
  });
}

let audioCtx = null;

/**
 * 內部：確保 AudioContext 在 iOS 17+ 被 resume() （需 user activation 後呼叫）
 */
async function ensureAudioContext() {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === "suspended") {
    try {
      await audioCtx.resume();
    } catch (e) {
      /* ignore */
    }
  }
}
